<template>
    <b-modal size="lg" id="challengeModal" v-model="showModal"
             :title="challengeId ? 'Update badge' : 'Add new badge'"
             @hidden="close">
        <b-container>
            <loading :active.sync="isLoading"
                     :z-index="99999"
                     :can-cancel="false"
                     :is-full-page="true"></loading>

            <form class="m-form m-form--label-align-right">
                <div class="m-portlet__body">
                    <div class="m-form__content">
                        <div class="m-alert m-alert--icon alert alert-danger" role="alert" v-if="error">
                            <div class="m-alert__icon"><i class="la la-warning"></i></div>
                            <div class="m-alert__text" v-html="message"></div>
                            <div class="m-alert__close">
                                <button type="button" class="close" @click="error=false" aria-label="Close"></button>
                            </div>
                        </div>
                    </div>

                    <div class="m-form__section m-form__section--first">
                        <div class="form-group m-form__group row">
                            <label class="col-lg-3 col-form-label">Name <span class="text-danger">*</span></label>
                            <div class="col-lg-9">
                                <input type="text" required v-model="name" class="form-control m-input"
                                       placeholder="e.g. 5 Miles, Cattails, ...">
                                <span class="m-form__help">Badge name shown in app gallery</span>
                            </div>
                        </div>

                        <div class="form-group m-form__group row">
                            <label class="col-lg-3 col-form-label">
                                Required distance (miles) <span class="text-danger">*</span>
                            </label>
                            <div class="col-lg-9">
                                <input type="number" min="0" step="0.1" required
                                       v-model.number="required_distance_miles"
                                       class="form-control m-input"
                                       placeholder="e.g. 5">
                                <span class="m-form__help">
                                    Auto-granted when user accumulates this many miles
                                </span>
                            </div>
                        </div>

                        <div class="form-group m-form__group row">
                            <label class="col-lg-3 col-form-label">Description</label>
                            <div class="col-lg-9">
                                <textarea v-model="description" class="form-control m-input" rows="3"
                                          placeholder="Short description shown to users"></textarea>
                            </div>
                        </div>

                        <div class="form-group m-form__group row">
                            <label class="col-lg-3 col-form-label">
                                Badge icon
                                <small class="d-block text-muted">
                                    PNG transparent recommended, 256×256+
                                </small>
                            </label>
                            <div class="col-lg-9">
                                <div v-if="badge_icon_preview" class="mb-2">
                                    <img :src="badge_icon_preview" alt="badge"
                                         style="max-height: 96px; max-width: 96px; border: 1px solid #ddd; padding: 4px;" />
                                </div>
                                <input type="file" id="badge_icon" name="badge_icon"
                                       accept="image/png,image/jpeg,image/svg+xml"
                                       v-on:change="handleFile('badge_icon')" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </b-container>
        <div slot="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModal=false">Close</button>
            <button type="button" class="btn btn-brand" @click="modifyChallenge">
                {{ challengeId ? 'Update' : 'Create' }}
            </button>
        </div>
    </b-modal>
</template>

<script>
    import ChallengeApi from '../../api/challenge';
    import FileHelper from '../../service/file';
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        components: { Loading },
        name: "challenge-modal",
        props: ['challengeId'],
        data() {
            return {
                fullPage: true,
                isLoading: false,
                success: false,
                error: false,
                message: null,
                showModal: false,
                id: this.challengeId,
                name: null,
                required_distance_miles: null,
                description: null,
                badge_icon: null,           // base64 data URL when newly picked
                badge_icon_preview: null,   // displayable url (existing icon or data URL)
                fileHelper: FileHelper,
            }
        },
        watch: {
            challengeId(val) {
                if (val) {
                    ChallengeApi.getChallengeDetail(this, val, (context) => {
                        if (context.challenge) {
                            this.name = context.challenge.name;
                            this.required_distance_miles = context.challenge.required_distance_miles;
                            this.description = context.challenge.description;
                            this.badge_icon = null;
                            this.badge_icon_preview = this.iconPreviewUrl(context.challenge.badge_icon);
                        }
                    })
                } else {
                    this.resetData();
                }
            },
        },
        methods: {
            modifyChallenge() {
                if (!this.name || this.required_distance_miles == null || this.required_distance_miles < 0) {
                    this.error = true;
                    this.message = 'Name and a non-negative required distance are required.';
                    return;
                }

                this.isLoading = true;
                this.error = false;

                let data = {
                    name: this.name,
                    required_distance_miles: this.required_distance_miles,
                    description: this.description,
                };
                // Only send badge_icon if user picked a new one; otherwise server keeps existing.
                if (this.badge_icon) {
                    data.badge_icon = this.badge_icon;
                }

                const done = (context) => {
                    this.isLoading = false;
                    if (context.success) {
                        this.close();
                        this.$emit(this.challengeId ? 'show-update-success-alert' : 'show-create-success-alert');
                    }
                };

                if (this.challengeId) {
                    ChallengeApi.updateChallenge(this, this.challengeId, data, done);
                } else {
                    ChallengeApi.createChallenge(this, data, done);
                }
            },
            resetData() {
                this.id = null;
                this.name = null;
                this.required_distance_miles = null;
                this.description = null;
                this.badge_icon = null;
                this.badge_icon_preview = null;
                const el = document.getElementById("badge_icon");
                if (el) el.value = "";
            },
            close() {
                this.$emit('clear-id');
                this.error = false;
                this.showModal = false;
            },
            handleFile(id) {
                const el = document.querySelector('#' + id);
                if (!el || !el.files || !el.files[0]) {
                    this.badge_icon = null;
                    return;
                }
                this.fileHelper.getBase64(el.files[0], this, (context) => {
                    if (context && context.currentFile) {
                        this.badge_icon = context.currentFile;
                        this.badge_icon_preview = context.currentFile;
                    } else {
                        el.value = null;
                    }
                });
            },
            // Build absolute URL for <img :src=...> preview.
            // Mirrors Android `buildBadgeIconUrl` exactly:
            //   final URL = window.apiUrl + '/assets' + (path.startsWith('/') ? path : '/' + path)
            iconPreviewUrl(raw) {
                if (!raw || typeof raw !== 'string') return null;
                if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
                    return raw;
                }
                const normalized = raw.startsWith('/') ? raw : '/' + raw;
                return (window.apiUrl || '') + '/assets' + normalized;
            }
        }
    }
</script>

<style scoped></style>
